import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Power, PowerOff } from 'lucide-react';
import ApiClient from '../utils/api';

const ApiToolsManagement = () => {
  const [tools, setTools] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    base_url: '',
    endpoint: '',
    headers: '{}'
  });

  useEffect(() => {
    loadTools();
  }, []);

  const loadTools = async () => {
    try {
      const response = await ApiClient.getApiTools();
      setTools(response.tools);
    } catch (error) {
      console.error('Failed to load tools:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTool) {
        await ApiClient.updateApiTool(editingTool.id, formData);
      } else {
        await ApiClient.createApiTool(formData);
      }
      
      setShowForm(false);
      setEditingTool(null);
      setFormData({ name: '', description: '', base_url: '', endpoint: '', headers: '{}' });
      await loadTools();
    } catch (error) {
      console.error('Failed to save tool:', error);
      alert('Failed to save tool. Please try again.');
    }
  };

  const handleEdit = (tool) => {
    setEditingTool(tool);
    setFormData({
      name: tool.name,
      description: tool.description || '',
      base_url: tool.base_url,
      endpoint: tool.endpoint || '',
      headers: tool.headers || '{}'
    });
    setShowForm(true);
  };

  const handleToggle = async (toolId) => {
    try {
      await ApiClient.toggleApiTool(toolId);
      await loadTools();
    } catch (error) {
      console.error('Failed to toggle tool:', error);
    }
  };

  const handleDelete = async (toolId) => {
    if (window.confirm('Are you sure you want to delete this tool?')) {
      try {
        await ApiClient.deleteApiTool(toolId);
        await loadTools();
      } catch (error) {
        console.error('Failed to delete tool:', error);
      }
    }
  };

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>API Tools Management</h1>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <Plus size={16} />
          Add Tool
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3>{editingTool ? 'Edit Tool' : 'Add New Tool'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Tool Name</label>
              <input 
                type="text"
                className="form-input"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea 
                className="form-input"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows="3"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Base URL</label>
              <input 
                type="url"
                className="form-input"
                value={formData.base_url}
                onChange={(e) => setFormData({...formData, base_url: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Endpoint (optional)</label>
              <input 
                type="text"
                className="form-input"
                value={formData.endpoint}
                onChange={(e) => setFormData({...formData, endpoint: e.target.value})}
                placeholder="/api/endpoint"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Headers (JSON)</label>
              <textarea 
                className="form-input"
                value={formData.headers}
                onChange={(e) => setFormData({...formData, headers: e.target.value})}
                rows="3"
                placeholder='{"Authorization": "Bearer token"}'
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="btn btn-primary">
                {editingTool ? 'Update' : 'Create'} Tool
              </button>
              <button 
                type="button" 
                className="btn"
                onClick={() => {
                  setShowForm(false);
                  setEditingTool(null);
                  setFormData({ name: '', description: '', base_url: '', endpoint: '', headers: '{}' });
                }}
                style={{ background: '#6c757d', color: 'white' }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <h3>API Tools</h3>
        {tools.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
            No API tools configured. Add your first tool to get started.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #ddd' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Description</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>URL</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tools.map(tool => (
                  <tr key={tool.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px', fontWeight: '500' }}>{tool.name}</td>
                    <td style={{ padding: '12px', color: '#666' }}>
                      {tool.description || 'No description'}
                    </td>
                    <td style={{ padding: '12px', fontSize: '12px', color: '#666' }}>
                      {tool.base_url}{tool.endpoint ? `/${tool.endpoint}` : ''}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        background: tool.active ? '#d4edda' : '#f8d7da',
                        color: tool.active ? '#155724' : '#721c24'
                      }}>
                        {tool.active ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleToggle(tool.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: tool.active ? '#dc3545' : '#28a745',
                            cursor: 'pointer'
                          }}
                          title={tool.active ? 'Disable' : 'Enable'}
                        >
                          {tool.active ? <PowerOff size={16} /> : <Power size={16} />}
                        </button>
                        <button
                          onClick={() => handleEdit(tool)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#007bff',
                            cursor: 'pointer'
                          }}
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(tool.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#dc3545',
                            cursor: 'pointer'
                          }}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiToolsManagement;